<template>
  <div class="programs-page">

    <section class="programs-hero">
      <div class="programs-hero-inner">
        <h1 class="programs-h1">Our programs.</h1>
        <p class="programs-hero-sub">
          Everything we run is built around one idea — that the only thing standing 
          between talented people and great tech careers should not be access.
        </p>
      </div>
    </section>

    <section class="programs-body">
      <div class="programs-body-inner">

        <div class="prog-item" :class="{ 'prog-item--open': open === 1 }">
          <button class="prog-trigger" @click="toggle(1)">
            <div class="prog-trigger-left">
              <span class="prog-num">01</span>
              <span class="prog-name">NexaScale Study Groups</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Community Learning</span>
              <span class="prog-chevron">{{ open === 1 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 1">
            <div class="prog-body-inner">
              <p>
                A virtual community for people to connect, learn, and build together. 
                We host bi-weekly office hours across multiple tracks, bringing in industry 
                experts to answer questions, unblock members, and push the learning forward.
              </p>
              <p>
                Tracks include Artificial Intelligence, Frontend Engineering, Backend Engineering, 
                Cloud Engineering, Data Science, Data Structures &amp; Algorithms, CyberSecurity, 
                and Mobile Engineering.
              </p>
              <router-link class="prog-cta" to="/programs/nexascale-study-group">Learn more →</router-link>
            </div>
          </div>
        </div>

        <div class="prog-item" :class="{ 'prog-item--open': open === 2 }">
          <button class="prog-trigger" @click="toggle(2)">
            <div class="prog-trigger-left">
              <span class="prog-num">02</span>
              <span class="prog-name">NexaScale Mentorship</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">AI Readiness · Cloud · Upskilling</span>
              <span class="prog-chevron">{{ open === 2 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 2">
            <div class="prog-body-inner">
              <p>
                A free 4-month mentorship program for entry-level engineers. Built around Cloud 
                Engineering and expanded to cover AI readiness, upskilling for modern engineering 
                roles, and cloud-native development — equipping members for the careers that 
                actually exist right now.
              </p>
              <p>
                Mentees build real projects using real-world scenarios, learn cloud security, 
                prep for technical interviews, and leave with a portfolio that gets them hired.
              </p>
              <router-link class="prog-cta" to="/programs/nexascale-mentorship">Learn more →</router-link>
            </div>
          </div>
        </div>

        <div class="prog-item" :class="{ 'prog-item--open': open === 3 }">
          <button class="prog-trigger" @click="toggle(3)">
            <div class="prog-trigger-left">
              <span class="prog-num">03</span>
              <span class="prog-name">NexaScale Apprenticeship</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Industry &amp; Academic Partnerships</span>
              <span class="prog-chevron">{{ open === 3 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 3">
            <div class="prog-body-inner">
              <p>
                Real work experience through structured apprenticeships with industry and academic 
                partners including Miva University, Co.Lab Canada, Alvative, and Genesys Tech Hub Enugu. 
                Members are placed in hands-on roles where they build real products and emerge genuinely hirable.
              </p>
              <router-link class="prog-cta" to="/programs/nexascale-apprenticeship">Learn more →</router-link>
            </div>
          </div>
        </div>

        <div class="prog-item" :class="{ 'prog-item--open': open === 4 }">
          <button class="prog-trigger" @click="toggle(4)">
            <div class="prog-trigger-left">
              <span class="prog-num">04</span>
              <span class="prog-name">NexaScale Hackathon</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Open Competition</span>
              <span class="prog-chevron">{{ open === 4 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 4">
            <div class="prog-body-inner">
              <p>
                An open hackathon exploring cybersecurity and AI security, focused on Cyber Threat 
                Detection. Participants of all skill levels collaborate with diverse teams, solve real 
                security challenges, and network with experts across cybersecurity and AI.
              </p>
              <!---<a class="prog-cta" href="https://forms.gle/ASVVWJQKAHFPgs2J7" target="_blank">Apply now →</a>-->
            </div>
          </div>
        </div>

        <div class="prog-item" :class="{ 'prog-item--open': open === 5 }">
          <button class="prog-trigger" @click="toggle(5)">
            <div class="prog-trigger-left">
              <span class="prog-num">05</span>
              <span class="prog-name">Future STEM Stars</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Girls in Tech · Nigeria</span>
              <span class="prog-chevron">{{ open === 5 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 5">
            <div class="prog-body-inner">
              <p>
                Structured AI and technology education delivered to girls in Nigerian public secondary 
                schools — in partnership with <a href="https://katwiganddale.com/" target="_blank">Katwig &amp; Dale</a>. 
                We show up before they've been told tech isn't for them, with mentors, curriculum, 
                and a peer community built to last beyond the classroom.
              </p>
              <router-link class="prog-cta" to="/programs/future-stem-stars">Learn more →</router-link>
            </div>
          </div>
        </div>

        <div class="prog-item" :class="{ 'prog-item--open': open === 6 }">
          <button class="prog-trigger" @click="toggle(6)">
            <div class="prog-trigger-left">
              <span class="prog-num">06</span>
              <span class="prog-name">Laptop Sharing Initiative</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Access &amp; Equity</span>
              <span class="prog-chevron">{{ open === 6 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 6">
            <div class="prog-body-inner">
              <p>
                Access to a laptop shouldn't determine whether someone can build a tech career. 
                We've partnered with hubs across Nigeria to house laptops in areas where our 
                members are most concentrated — giving people the tools they need while they 
                work toward affording their own.
              </p>
              <a class="prog-cta" href="https://laptopsharing.nexascale.org/" target="_blank">Learn more →</a>
            </div>
          </div>
        </div>

        <div class="prog-item prog-item--labs" :class="{ 'prog-item--open': open === 7 }">
          <button class="prog-trigger" @click="toggle(7)">
            <div class="prog-trigger-left">
              <span class="prog-num">07</span>
              <span class="prog-name">NexaScale Labs</span>
            </div>
            <div class="prog-trigger-right">
              <span class="prog-tag">Product Incubation</span>
              <span class="prog-chevron">{{ open === 7 ? '−' : '+' }}</span>
            </div>
          </button>
          <div class="prog-body" v-show="open === 7">
            <div class="prog-body-inner">
              <p>
                The product incubation arm of NexaScale — where community members go beyond 
                learning and start shipping. Labs builds real technology for real people, starting 
                with the communities we know best.
              </p>
              <p>
                Our first product is <strong>Kowaara</strong>, an LLM-powered learning platform 
                that transforms study materials into personalised experiences — flashcards, ELI5 
                summaries, mock quizzes, and more — built mobile-first for learners across 
                emerging markets.
              </p>
              <a class="prog-cta" href="https://labs.nexascale.org" target="_blank">Visit Labs →</a>
            </div>
          </div>
        </div>

      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'ProgramsView',
  data() {
    return {
      open: 1
    }
  },
  methods: {
    toggle(id) {
      this.open = this.open === id ? null : id
    }
  }
}
</script>

<style scoped>
.programs-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.programs-page * { box-sizing: border-box; }
.programs-page p, .programs-page h1, .programs-page h2 { text-align: left; }

/* HERO */
.programs-hero {
  background: #191616;
  padding: 100px 7% 80px;
}
.programs-hero-inner { max-width: 680px; }
.programs-h1 {
  font-size: clamp(3.2rem, 8vw, 7rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.0;
  color: #ffffff;
  margin: 0 0 24px;
  word-break: break-word;
}
.programs-hero-sub {
  font-size: 1rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.5);
  margin: 0;
}

/* BODY */
.programs-body {
  background: #ffffff;
  padding: 0 7% 100px;
}
.programs-body-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding-top: 60px;
}

/* EACH PROGRAM ITEM */
.prog-item {
  border-bottom: 1px solid #e8e4e0;
}
.prog-item--labs {
  background: #FFFBEB;
  margin: 0 -7%;
  padding: 0 7%;
}

.prog-trigger {
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 28px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  font-family: inherit;
  text-align: left;
  transition: opacity 0.15s;
}
.prog-trigger:hover { opacity: 0.7; }

.prog-trigger-left {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}
.prog-num {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #FFB812;
  flex-shrink: 0;
}
.prog-name {
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  font-weight: 700;
  color: #191616;
  letter-spacing: -0.01em;
  word-break: break-word;
}
.prog-item--open .prog-name { color: #FF9914; }

.prog-trigger-right {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}
.prog-tag {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c0bbb8;
  white-space: nowrap;
}
.prog-chevron {
  font-size: 1.4rem;
  font-weight: 300;
  color: #c0bbb8;
  width: 24px;
  text-align: center;
  line-height: 1;
  transition: color 0.2s;
}
.prog-item--open .prog-chevron { color: #FFB812; }

/* BODY */
.prog-body { overflow: hidden; }
.prog-body-inner {
  padding: 0 0 36px 44px;
  max-width: 640px;
}
.prog-body-inner p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 14px;
}
.prog-body-inner a {
  color: #FF9914;
  text-decoration: underline;
  text-decoration-color: rgba(255,153,20,0.3);
}
.prog-body-inner strong { font-weight: 700; color: #191616; }

.prog-cta {
  display: inline-block;
  font-size: 0.88rem;
  font-weight: 700;
  color: #191616 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #FFB812;
  padding-bottom: 2px;
  margin-top: 8px;
  transition: color 0.2s;
}
.prog-cta:hover { color: #FF9914 !important; }

/* RESPONSIVE */
@media (max-width: 768px) {
  .programs-hero { padding: 80px 5% 60px; }
  .programs-body { padding: 0 5% 80px; }
  .prog-tag { display: none; }
  .prog-item--labs { margin: 0 -5%; padding: 0 5%; }
}
</style>